import type { RowVariantDefinition, SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { sharedChatAnswerVariantMetadata } from '@/deskUiSkeletons/rowTemplates/RowVariantRegistry'

function initial(value: string) {
  return String(value || '?').trim().slice(0, 1).toUpperCase() || '?'
}

function attachmentLabel(attachment: SubTemplateProps['display']['media']['attachments'][number]) {
  return attachment.title || attachment.filename || attachment.storedName || 'Attachment'
}

export function ChatTemplate({ display, controls, slots }: SubTemplateProps) {
  const author = display.identity.author || display.identity.address || display.title || 'Anonymous'
  const hasChildren = display.counts.answers > 0 || display.counts.submsges > 0

  return (
    <>
      <article
        className={`md-chat-row group mx-2 mb-1.5 rounded-lg border px-3 py-2.5 font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] text-[13px] transition-colors max-[640px]:mx-0 max-[640px]:rounded-none max-[640px]:border-x-0${display.isChecked ? ' border-sky-400 bg-sky-50' : ' border-slate-200 bg-white hover:bg-slate-50/70'}${display.state.isRead ? '' : ' border-l-[3px] border-l-sky-500'}`}
        data-md-sub-template="chat"
      >
        <div className="grid grid-cols-[32px_minmax(0,1fr)] gap-2.5">
          <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-700 text-[11px] font-semibold text-white">
            <span aria-hidden="true">{initial(author)}</span>
            {display.media.avatarPath ? (
              <img
                src={display.media.avatarPath}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                onError={(event) => { event.currentTarget.style.display = 'none' }}
              />
            ) : null}
            {display.permissions.canCheck ? (
              <span className="absolute inset-0 z-10 opacity-0 transition-opacity group-hover:opacity-100 [&_.md-sub-icon]:absolute [&_.md-sub-icon]:inset-0 [&_.md-sub-icon]:m-0 [&_.md-sub-icon]:h-full [&_.md-sub-icon]:w-full [&_.md-sub-icon]:rounded-full [&_.md-sub-icon]:border-0 [&_.md-sub-icon]:bg-slate-950/35">
                {controls.actions.check}
              </span>
            ) : null}
          </div>

          <div className="min-w-0">
            {/* Sender line = the shared skeleton control (controls.emailFrom);
                this variant opts in and styles its structural classes. */}
            <header className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 leading-5 [&_.md-sub-email-from]:contents [&_.md-sub-email-from__author]:min-w-0 [&_.md-sub-email-from__author]:truncate [&_.md-sub-email-from__author]:font-semibold [&_.md-sub-email-from__author]:text-slate-900 [&_.md-sub-email-from__address]:min-w-0 [&_.md-sub-email-from__address]:truncate [&_.md-sub-email-from__address]:text-[11px] [&_.md-sub-email-from__address]:text-slate-500">
              {controls.emailFrom}
              <span className="text-[11px] text-slate-400">{controls.content.time}</span>
              {!display.state.isRead ? <span className="h-1.5 w-1.5 rounded-full bg-sky-500" title="Unread" /> : null}
            </header>

            <div className="mt-1 break-words text-[13px] leading-5 text-slate-700 [overflow-wrap:anywhere] [&_.md-sub-text]:whitespace-normal [&_.md-sub-text_a]:text-sky-700 [&_.md-sub-text_img]:max-w-full">
              {controls.content.text}
            </div>

            {display.media.attachments.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {display.media.attachments.map((attachment, index) => {
                  const label = attachmentLabel(attachment)
                  const preview = attachment.thumbSrc ? (
                    <img src={attachment.thumbSrc} alt="" className="h-14 w-20 rounded-md object-cover" />
                  ) : (
                    <span className="max-w-48 truncate px-2 py-1.5 text-xs text-slate-600">{label}</span>
                  )

                  return attachment.src ? (
                    <a key={`${attachment.id}-${index}`} href={attachment.src} title={label} className="overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-slate-300">
                      {preview}
                    </a>
                  ) : (
                    <span key={`${attachment.id}-${index}`} title={label} className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                      {preview}
                    </span>
                  )
                })}
              </div>
            ) : null}

            <footer className="mt-1.5 flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 [&_.md-sub-icon]:m-0 [&_.md-sub-meta-link]:m-0 [&_.md-sub-meta-link]:text-[11px] [&_.md-sub-meta-link]:font-medium [&_.md-sub-meta-link]:text-slate-500 hover:[&_.md-sub-meta-link]:text-sky-700">
              {controls.actions.quickReply}
              {controls.actions.favorite}
              {controls.links.answers}
              {controls.links.submsges}
            </footer>
          </div>
        </div>
      </article>

      <div className={`md-chat-children ml-6 border-l border-slate-200 pl-2.5 max-[640px]:ml-3${hasChildren ? '' : ' hidden'}`}>
        {slots.allChildren}
      </div>
    </>
  )
}

export const sharedChatAnswerVariant = {
  ...sharedChatAnswerVariantMetadata,
  Template: ChatTemplate,
} as const satisfies RowVariantDefinition
